/**
 * The first week's words (v1 ticket 37): the honest promise and the firsts, as lines for the end of a round, a
 * tending round or a lesson in test mode. The server sends counts per material and the firsts' words, never a box
 * (`POST /api/learn/end`); this only puts them into the interface's words.
 *
 * DOM-free and dictionary-free: the caller passes its translator, so the unit test can run it on the real strings
 * of both interfaces without a browser.
 */
import type { Comeback, FirstKind, HonestPromise, PromiseMaterial } from './types';

export type Tr = (key: string, vars?: Record<string, string | number>) => string;

/** The materials in the order the promise names them. */
const ORDER: readonly PromiseMaterial[] = ['wood', 'stone', 'gold'];

/** "6 houses can become wood and 2 can become stone": the first material says "houses", the others only the count. */
export function promiseList(counts: HonestPromise['counts'], tr: Tr): string {
  const parts = ORDER.filter((m) => (counts[m] ?? 0) > 0).map((m, i) => {
    const n = counts[m] ?? 0;
    const material = tr(`round.material.${m}`);
    if (i > 0) return tr('week.more', { n, material });
    return n === 1 ? tr('week.house', { material }) : tr('week.houses', { n, material });
  });
  if (parts.length <= 1) return parts[0] ?? '';
  return parts.slice(0, -1).join(tr('week.sep')) + tr('week.and') + parts[parts.length - 1];
}

/**
 * The promise's day by name, in the interface's language and calendar: its weekday, and its date as well when it
 * is a week or more away, where a weekday alone would point at the wrong week.
 */
export function dayName(day: string, inDays: number, lang: string): string {
  const [y, m, d] = day.split('-').map(Number);
  const noon = new Date(Date.UTC(y, m - 1, d, 12));
  const opts: Intl.DateTimeFormatOptions = inDays >= 7
    ? { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }
    : { weekday: 'long', timeZone: 'UTC' };
  return noon.toLocaleDateString(lang, opts);
}

/**
 * The promise's one line: "By this time tomorrow in your town: …." or "Next in your town: … on Thursday."; null with
 * nothing to say. A named town (v1 ticket 40) is named, the line holding `{town}` for the caller to put the name in,
 * isolated.
 */
export function promiseLine(p: HonestPromise | null, tr: Tr, lang: string, town: string | null = null): string | null {
  if (!p) return null;
  const list = promiseList(p.counts, tr);
  if (!list) return null;
  const named: Record<string, string> = town ? { town: '{town}' } : {};
  return p.window === 'tomorrow'
    ? tr(town ? 'week.promiseIn' : 'week.promise', { list, ...named })
    : tr(town ? 'week.nextIn' : 'week.next', { list, day: dayName(p.day, p.inDays, lang), ...named });
}

/**
 * A first's string, holding `{word}` for the caller to put the word in, isolated. The first word out of the fog
 * brings the calendar; the others, the first line where the learner knows every word (v1 ticket 31) among them,
 * have a line each.
 */
export function firstKey(kind: FirstKind): string {
  return kind === 'fog' ? 'week.calendar' : `week.first.${kind}`;
}

/**
 * A first's line split around its word, for the caller to put the word between them, isolated. A word or line that
 * closes on its own full stop, question or exclamation mark keeps it, and the line's full stop after it goes.
 */
export function aroundWord(line: string, word: string): [string, string] {
  const [pre, post = ''] = line.split('{word}');
  return [pre, /[.!?؟…]$/.test(word.trim()) && post.startsWith('.') ? post.slice(1) : post];
}

/** How long ago a moment was made, in the interface's words: "last week", "2 weeks ago", "last month". */
export function agoPhrase(days: number, lang: string): string {
  const f = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
  if (days >= 365) return f.format(-Math.round(days / 365), 'year');
  if (days >= 30) return f.format(-Math.round(days / 30), 'month');
  if (days >= 7) return f.format(-Math.round(days / 7), 'week');
  return f.format(-days, 'day');
}

/**
 * An old recording coming back (v1 ticket 31): "You now know every word in 3 lines of {title}, from 2 weeks ago.",
 * holding `{title}` for the caller to put the moment's title in, isolated.
 */
export function comebackLine(c: Pick<Comeback, 'lines' | 'days'>, tr: Tr, lang: string): string {
  return tr(c.lines === 1 ? 'week.comebackOne' : 'week.comeback', { n: c.lines, ago: agoPhrase(c.days, lang), title: '{title}' });
}
